import { ColumnDef } from "@tanstack/react-table";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { enumToStringConverter } from "../../../helpers/appHelpers";
import { TimeRequestStatus } from "../../../models/time-request.dto";
import { WorkTimeUnit } from "../../../models/employee-work-time.dto";

export type TimeRequestKind = "workTime" | "overtime";

// Which requests a list shows: the user's own, those awaiting their
// response, or every request in the company (HR)
export type TimeRequestListMode = "SELF" | "APPROVALS" | "ALL";

export const TIME_REQUEST_LABELS: Record<TimeRequestKind, {
	name: string;
	submit: string;
}> = {
	workTime: { name: "Work Time Request", submit: "Submit Work Time" },
	overtime: { name: "Overtime Request", submit: "Request Overtime" },
};

export const getTimeRequestStatusColor = (status?: string) => {
	switch (status) {
		case TimeRequestStatus.APPROVED:
			return "#4CAF50";
		case TimeRequestStatus.CANCELLED:
			return "#808080";
		case TimeRequestStatus.DECLINED:
			return "#F44336";
		case TimeRequestStatus.PENDING:
			return "#FFC107";
		case TimeRequestStatus.REVOKED:
			return "#9C27B0";
		default:
			return "#2196F3";
	}
};

export const formatTime = (value?: number, unit?: WorkTimeUnit) => {
	if (value === undefined || value === null) return "--";
	const unitName = unit === WorkTimeUnit.DAY ? "day" : "hour";
	return `${value} ${unitName}${value === 1 ? "" : "s"}`;
};

const formatDate = (value?: string | null) =>
	value ? new Date(value).toLocaleDateString() : "--";

export const StatusBadge = ({ status }: { status?: string }) => (
	<div
		className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden inline-flex justify-center"
		style={{ color: getTimeRequestStatusColor(status) }}
	>
		<div
			className="absolute inset-0"
			style={{ background: getTimeRequestStatusColor(status), opacity: 0.15 }}
		/>
		<p className="text-xs">{enumToStringConverter(status || "")}</p>
	</div>
);

/** Approval progress, e.g. "Level 2 of 3" while pending. */
export const approvalProgress = (request: any) => {
	if (request?.status !== TimeRequestStatus.PENDING) return "--";
	return `Level ${request.currentLevel ?? 1} of ${request.approvalsRequired ?? 1}`;
};

const employeeColumn: ColumnDef<any> = {
	accessorKey: "employee",
	header: "Employee",
	size: 250,
	minSize: 200,
	maxSize: 400,
	cell: ({ getValue }) => {
		const employee = getValue() as any;
		if (!employee) return "--";
		return (
			<div className="flex items-center space-x-4">
				<img
					src={employee?.photoUrl || "https://via.placeholder.com/40"}
					alt="Employee"
					className="w-10 h-10 rounded-full object-cover"
				/>
				<p className="truncate">
					{(employee?.firstName || "") + " " + (employee?.lastName || "")}
				</p>
			</div>
		);
	},
};

const commonColumns: ColumnDef<any>[] = [
	{
		accessorKey: "status",
		header: "Status",
		size: 180,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => <StatusBadge status={getValue() as string} />,
	},
	{
		id: "approval",
		header: "Approval",
		size: 160,
		minSize: 130,
		maxSize: 220,
		cell: ({ row }) => approvalProgress(row.original),
	},
	{
		accessorKey: "createdAt",
		header: "Date Submitted",
		size: 180,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => formatDate(getValue() as string),
	},
	{
		id: "actions",
		header: "Actions",
		size: 100,
		minSize: 80,
		maxSize: 150,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						meta?.onActionClick?.(row.original, e);
					}}
					className="p-2 hover:bg-gray-100 rounded-full transition-colors"
				>
					<BiDotsHorizontalRounded className="text-xl" />
				</button>
			);
		},
	},
];

const workTimeColumns: ColumnDef<any>[] = [
	{
		accessorKey: "payPeriod",
		header: "Pay Period",
		size: 180,
		minSize: 140,
		maxSize: 250,
		cell: ({ getValue }) => (getValue() as any)?.code || "--",
	},
	{
		accessorKey: "timeValue",
		header: "Time Worked",
		size: 160,
		minSize: 130,
		maxSize: 220,
		cell: ({ row }) => formatTime(row.original.timeValue, row.original.timeUnit),
	},
	...commonColumns,
];

const overtimeColumns: ColumnDef<any>[] = [
	{
		accessorKey: "overtime",
		header: "Overtime",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => (getValue() as any)?.name || "--",
	},
	{
		accessorKey: "numberOfHours",
		header: "Hours",
		size: 120,
		minSize: 100,
		maxSize: 180,
		cell: ({ getValue }) => formatTime(getValue() as number, WorkTimeUnit.HOUR),
	},
	{
		accessorKey: "payPeriod",
		header: "Pay Period",
		size: 180,
		minSize: 140,
		maxSize: 250,
		cell: ({ getValue }) => (getValue() as any)?.code || "--",
	},
	...commonColumns,
];

export const getTimeRequestColumns = (
	kind: TimeRequestKind,
	withEmployee: boolean,
): ColumnDef<any>[] => {
	const columns = kind === "workTime" ? workTimeColumns : overtimeColumns;
	return withEmployee ? [employeeColumn, ...columns] : columns;
};
